import { QueueView } from '@hone-automation/common';
import { StatusValue, PriorityValue, AssigneeValue } from '../../../contexts';

/**
 * Drop rules matrix - defines what operations happen when dragging from source to target filter
 *
 * | Source → Target | Me | My Team | For Client | Completed | Blockers |
 * |-----------------|:--:|:-------:|:----------:|:---------:|:--------:|
 * | Me              | ❌ | ✅ Unassign | ✅ Unassign+InProgress | ✅ Closed | ✅ +Blocker |
 * | My Team         | ✅ Assign | ❌ | ✅ InProgress | ✅ Closed | ✅ +Blocker |
 * | For Client      | ✅ Assign+Open | ✅ Unassign+Open | ❌ | ✅ Closed | ✅ +Blocker |
 * | Completed       | ✅ Assign+Open | ✅ Unassign+Open | ✅ In Progress | ❌ | ✅ Open+Blocker |
 * | Blockers        | ✅ Assign-Blocker | ✅ Unassign-Blocker | ✅ -Blocker | ✅ Closed-Blocker | ❌ |
 *
 * Notes:
 * - Me = assigned to current user → dragging here assigns to me
 * - My Team = unassigned/assigned to others → dragging here unassigns
 * - Client = In Progress status → dragging here sets In Progress
 * - Blockers toggle = priority: 'Blocker' or 'Normal'
 */

export interface DropOperation {
  /** Whether the drop is allowed */
  allowed: boolean;
  /** Status change to apply (if any) */
  setStatus?: StatusValue;
  /** Priority change to apply (if any) */
  setPriority?: PriorityValue;
  /** Assignee change - 'me' = current user, null = unassign */
  setAssignee?: AssigneeValue;
  /** Parent group ID change - group ID to add to group, null to remove from group */
  setParentId?: string | null;
}

/** Invalid drop - operation not allowed */
const INVALID: DropOperation = { allowed: false };

/** Set status to Open */
const TO_OPEN: DropOperation = { allowed: true, setStatus: 'Open' };

/** Set status to In Progress */
const TO_IN_PROGRESS: DropOperation = { allowed: true, setStatus: 'In Progress' };

/** Set status to Closed */
const TO_CLOSED: DropOperation = { allowed: true, setStatus: 'Closed' };

/** Add Blocker priority */
const ADD_BLOCKER: DropOperation = { allowed: true, setPriority: 'Blocker' };

/** Remove Blocker priority (set to Normal) */
const REMOVE_BLOCKER: DropOperation = { allowed: true, setPriority: 'Normal' };

/** Open + Add Blocker (for completed items going to blockers) */
const OPEN_AND_BLOCKER: DropOperation = { allowed: true, setStatus: 'Open', setPriority: 'Blocker' };

/** Close + Remove Blocker */
const CLOSE_AND_REMOVE_BLOCKER: DropOperation = { allowed: true, setStatus: 'Closed', setPriority: 'Normal' };

/** Assign to me (current user) */
const ASSIGN_TO_ME: DropOperation = { allowed: true, setAssignee: 'me' };

/** Unassign */
const UNASSIGN: DropOperation = { allowed: true, setAssignee: null };

/** Unassign + In Progress */
const UNASSIGN_IN_PROGRESS: DropOperation = { allowed: true, setAssignee: null, setStatus: 'In Progress' };

/** Assign to me + Open */
const ASSIGN_OPEN: DropOperation = { allowed: true, setAssignee: 'me', setStatus: 'Open' };

/** Unassign + Open */
const UNASSIGN_OPEN: DropOperation = { allowed: true, setAssignee: null, setStatus: 'Open' };

/** Assign to me + Remove Blocker */
const ASSIGN_REMOVE_BLOCKER: DropOperation = { allowed: true, setAssignee: 'me', setPriority: 'Normal' };

/** Unassign + Remove Blocker */
const UNASSIGN_REMOVE_BLOCKER: DropOperation = { allowed: true, setAssignee: null, setPriority: 'Normal' };

/**
 * Drop rules matrix
 * Rows: source filter
 * Columns: target filter
 */
const DROP_RULES: Record<QueueView, Record<QueueView, DropOperation>> = {
  Me: {
    Me: INVALID,
    'My Team': UNASSIGN,
    Client: UNASSIGN_IN_PROGRESS,
    Completed: TO_CLOSED,
    Blockers: ADD_BLOCKER,
    Groups: INVALID, // Groups are handled separately by group ID
  },
  'My Team': {
    Me: ASSIGN_TO_ME,
    'My Team': INVALID,
    Client: TO_IN_PROGRESS,
    Completed: TO_CLOSED,
    Blockers: ADD_BLOCKER,
    Groups: INVALID,
  },
  Client: {
    Me: ASSIGN_OPEN,
    'My Team': UNASSIGN_OPEN,
    Client: INVALID,
    Completed: TO_CLOSED,
    Blockers: ADD_BLOCKER,
    Groups: INVALID,
  },
  Completed: {
    Me: ASSIGN_OPEN,
    'My Team': UNASSIGN_OPEN,
    Client: TO_IN_PROGRESS,
    Completed: INVALID,
    Blockers: OPEN_AND_BLOCKER,
    Groups: INVALID,
  },
  Blockers: {
    Me: ASSIGN_REMOVE_BLOCKER,
    'My Team': UNASSIGN_REMOVE_BLOCKER,
    Client: REMOVE_BLOCKER,
    Completed: CLOSE_AND_REMOVE_BLOCKER,
    Blockers: INVALID,
    Groups: INVALID,
  },
  Groups: {
    Me: ASSIGN_TO_ME,
    'My Team': UNASSIGN,
    Client: TO_IN_PROGRESS,
    Completed: TO_CLOSED,
    Blockers: ADD_BLOCKER,
    Groups: INVALID,
  },
};

/**
 * Get the operation to perform when dropping from source filter to target filter
 * @param source - Source filter view
 * @param target - Target filter view or group ID
 * @param isGroupId - Whether the target is a group ID (not a standard filter)
 */
export const getDropOperation = (
  source: QueueView | undefined,
  target: QueueView | string,
  isGroupId: boolean = false
): DropOperation => {
  // If target is a group ID, set parentId to that group
  if (isGroupId) {
    return { allowed: true, setParentId: target };
  }

  if (!source) {
    // No source filter means items are from the full list - allow all drops
    return { allowed: true };
  }

  return DROP_RULES[source]?.[target as QueueView] ?? INVALID;
};

/**
 * Check if a drop is allowed from source to target
 * @param source - Source filter view
 * @param target - Target filter view or group ID
 * @param isGroupId - Whether the target is a group ID
 */
export const canDropOnFilter = (
  source: QueueView | undefined,
  target: QueueView | string,
  isGroupId: boolean = false
): boolean => {
  return getDropOperation(source, target, isGroupId).allowed;
};

/**
 * Get all valid drop targets for a given source filter
 */
export const getValidDropTargets = (source: QueueView | undefined): QueueView[] => {
  const allFilters: QueueView[] = ['Me', 'My Team', 'Client', 'Completed', 'Blockers'];

  if (!source) {
    return allFilters;
  }

  return allFilters.filter(target => canDropOnFilter(source, target));
};
